/**
 * StreakCelebration - Fire/flame animation for hot streaks
 *
 * Features:
 * - Animated flames for streak milestones
 * - Streak count with multiplier display
 * - "Don't break your streak!" reminder
 * - Hot streak visual effects
 * - Milestone celebrations
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { cn } from '@/lib/utils';
import {
  Flame,
  Star,
  Zap,
  TrendingUp,
  AlertCircle,
  X,
  Calendar,
  Target,
  Trophy,
} from 'lucide-react';

// Streak milestone definitions
const STREAK_MILESTONES = [
  { days: 3, emoji: '🌱', label: 'Getting Started', multiplier: 1.1 },
  { days: 7, emoji: '🔥', label: 'One Week', multiplier: 1.25 },
  { days: 14, emoji: '⚡', label: 'Two Weeks', multiplier: 1.5 },
  { days: 21, emoji: '💪', label: 'Three Weeks', multiplier: 1.75 },
  { days: 30, emoji: '🏆', label: 'One Month', multiplier: 2.0 },
  { days: 60, emoji: '🌟', label: 'Two Months', multiplier: 2.5 },
  { days: 90, emoji: '👑', label: 'Three Months', multiplier: 3.0 },
];

// Get current milestone info
const getStreakMilestone = (streakCount) => {
  const milestones = [...STREAK_MILESTONES].reverse();
  return milestones.find(m => streakCount >= m.days) || STREAK_MILESTONES[0];
};

// Flame particle component
const FlameParticle = ({ delay, left }) => (
  <motion.div
    initial={{ y: 0, opacity: 0, scale: 0 }}
    animate={{
      y: [-40, -120],
      opacity: [0, 1, 0],
      scale: [0.5, 1, 0.3],
      x: [0, Math.random() * 20 - 10, Math.random() * 40 - 20]
    }}
    transition={{
      duration: 2,
      delay,
      repeat: Infinity,
      ease: 'easeOut'
    }}
    className="absolute bottom-0"
    style={{ left: `${left}%` }}
  >
    <Flame className="w-6 h-6 text-orange-400" />
  </motion.div>
);

const StreakCelebration = ({
  isOpen,
  onClose,
  streakCount,
  streakType = 'activity',
  isMilestone = false,
  bonusXP = 0,
  timeUntilReset, // { hours, minutes }
}) => {
  const [showContent, setShowContent] = useState(false);
  const milestone = getStreakMilestone(streakCount);
  const isHotStreak = streakCount >= 7;

  // Trigger confetti for milestones
  useEffect(() => {
    if (isOpen && isMilestone) {
      const colors = ['#FF6B6B', '#FF8E53', '#FFA600', '#FFD700'];

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors,
        shapes: ['circle', 'square'],
        scalar: 1.2,
      });

      // Follow-up burst
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
        });
      }, 250);
    }
  }, [isOpen, isMilestone]);

  // Animation sequence
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }}
          className="relative z-10 max-w-md w-full"
        >
          {/* Close Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute -top-10 right-0 p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </motion.button>

          {/* Card */}
          <div className="bg-gradient-to-b from-orange-900/90 via-red-900/80 to-gray-900 rounded-2xl overflow-hidden border-2 border-orange-500/50 shadow-2xl shadow-orange-500/30">
            {/* Fire Border Animation */}
            <div className="h-1.5 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 animate-pulse" />

            {/* Content */}
            <div className="relative p-6">
              {/* Background Flames */}
              {isHotStreak && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <FlameParticle
                      key={i}
                      delay={i * 0.3}
                      left={10 + i * 12}
                    />
                  ))}
                </div>
              )}

              {/* Top Section */}
              <div className="relative text-center mb-6">
                {/* Main Flame Icon */}
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="inline-block"
                >
                  <div className={cn(
                    'relative p-6 rounded-full',
                    isMilestone
                      ? 'bg-gradient-to-br from-orange-500 via-red-500 to-orange-600'
                      : 'bg-gradient-to-br from-orange-500/80 to-red-500/80'
                  )}>
                    <Flame className="w-16 h-16 text-white" />

                    {/* Glow effect */}
                    <motion.div
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.5, 0.8, 0.5]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                      className="absolute inset-0 bg-orange-500/50 rounded-full blur-xl"
                    />
                  </div>

                  {/* Milestone Crown */}
                  {isMilestone && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 15,
                        delay: 0.3
                      }}
                      className="absolute -top-2 -right-2 text-4xl"
                    >
                      {milestone.emoji}
                    </motion.div>
                  )}
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={cn(
                    'mt-4 text-3xl font-bold',
                    isMilestone ? 'text-orange-300' : 'text-orange-400'
                  )}
                >
                  {isMilestone ? '🎉 Streak Milestone!' : '🔥 Hot Streak!'}
                </motion.h2>

                {/* Streak Count */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                    delay: 0.4
                  }}
                  className="mt-4"
                >
                  <div className="text-6xl font-black text-white">
                    {streakCount}
                  </div>
                  <div className="text-lg text-orange-300/80">
                    Day{streakCount !== 1 ? 's' : ''} in a Row
                  </div>
                </motion.div>

                {/* Milestone Label */}
                {isMilestone && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/30 border border-orange-400/50"
                  >
                    <Trophy className="w-5 h-5 text-orange-300" />
                    <span className="font-bold text-orange-200">{milestone.label}</span>
                  </motion.div>
                )}
              </div>

              {/* Multiplier & Bonus */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="space-y-3"
              >
                {/* XP Multiplier */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/10 border border-yellow-500/30">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-gray-300">XP Multiplier</span>
                  </div>
                  <span className="text-xl font-bold text-yellow-400">
                    {milestone.multiplier}x
                  </span>
                </div>

                {/* Bonus XP */}
                {bonusXP > 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.9 }}
                    className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30"
                  >
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-gray-300">Bonus XP</span>
                    </div>
                    <span className="text-xl font-bold text-green-400">
                      +{bonusXP}
                    </span>
                  </motion.div>
                )}
              </motion.div>

              {/* Warning Section */}
              {timeUntilReset && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="mt-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div>
                      <div className="font-semibold text-yellow-300">Don't Break Your Streak!</div>
                      <div className="text-sm text-yellow-400/80 mt-1">
                        Complete an assignment within {timeUntilReset.hours}h {timeUntilReset.minutes}m
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Motivation Message */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="mt-4 text-center"
              >
                <p className="text-sm text-orange-300/80">
                  {streakCount < 7 ? (
                    <>Keep it up! You're building momentum 💪</>
                  ) : streakCount < 30 ? (
                    <>You're on fire! Don't let it die! 🔥</>
                  ) : streakCount < 90 ? (
                    <>Incredible dedication! You're unstoppable! ⚡</>
                  ) : (
                    <>LEGENDARY STREAK! You're a machine! 🏆</>
                  )}
                </p>
              </motion.div>

              {/* Action Button */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="mt-6 w-full py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold hover:from-orange-600 hover:to-red-600 transition-all shadow-lg shadow-orange-500/30"
              >
                Keep the Streak Alive!
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default StreakCelebration;
